import React from 'react';
import '../styles/components/PaginationUI.css';

export interface PaginationUIProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  variant?: 'default' | 'compact' | 'mobile';
  className?: string;
  showPageInfo?: boolean;
  disabled?: boolean;
  totalItems?: number;
  itemsPerPage?: number;
  showItemRange?: boolean;
  showPageNumbers?: boolean;
}

const PaginationUI: React.FC<PaginationUIProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  variant = 'default',
  className = '',
  showPageInfo = true,
  disabled = false,
  totalItems,
  itemsPerPage,
  showItemRange = true,
  showPageNumbers = true
}) => {
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;
  const hasPages = totalPages > 1;

  // Calculate item ranges
  const startItem = totalItems && itemsPerPage ? (currentPage - 1) * itemsPerPage + 1 : null;
  const endItem = totalItems && itemsPerPage ? Math.min(currentPage * itemsPerPage, totalItems) : null;

  if (!hasPages) {
    return null; // Don't render pagination if there's only one page
  }

  const handlePrevious = () => {
    if (!disabled && !isFirstPage) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (!disabled && !isLastPage) {
      onPageChange(currentPage + 1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, action: 'previous' | 'next') => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (action === 'previous') {
        handlePrevious();
      } else {
        handleNext();
      }
    }
  };

  // Generate pagination info text
  const getPaginationInfo = () => {
    if (showItemRange && totalItems && startItem && endItem) {
      if (showPageNumbers) {
        return `Page ${currentPage} of ${totalPages} • ${startItem}-${endItem} of ${totalItems}`;
      } else {
        return `${startItem}-${endItem} of ${totalItems}`;
      }
    }
    if (showPageInfo) {
      return `Page ${currentPage} of ${totalPages}`;
    }
    return null;
  };

  const paginationInfo = getPaginationInfo();

  return (
    <div className={`pagination-ui pagination-ui--${variant} ${className}`}>
      <button 
        className="pagination-button pagination-button--previous"
        onClick={handlePrevious}
        disabled={disabled || isFirstPage}
        aria-label="Go to previous page"
        onKeyDown={(e) => handleKeyDown(e, 'previous')}
      >
        Previous
      </button>
      
      {paginationInfo && (
        <span className="pagination-info">
          {paginationInfo}
        </span>
      )}
      
      <button 
        className="pagination-button pagination-button--next"
        onClick={handleNext}
        disabled={disabled || isLastPage}
        aria-label="Go to next page"
        onKeyDown={(e) => handleKeyDown(e, 'next')}
      >
        Next
      </button>
    </div>
  );
};

export default PaginationUI; 